// geometryToPolygon.ts

// Enkel type for 2D-polygon
export type IPolygonPoint = [number, number];
export type IPolygon = IPolygonPoint[];

export interface ComputeFootprintPolygonParams {
  /** Flat liste med XYZ-verdier: [x0, y0, z0, x1, y1, z1, ...] */
  vertexData: readonly number[];

  /** Indeksbuffer som beskriver trekanter: [i0, i1, i2, i3, i4, i5, ...] */
  indexData: readonly number[];

  /** 4x4 transformasjonsmatrise (lengde 16), f.eks. fra IFC */
  transform: readonly number[];

  /** IFC ExpressID – kun for logging/debug */
  expressId: number;

  /** IFC GlobalId – kun for logging/debug */
  globalId: string;
}

/**
 * Konverterer 3D-geometri (ett mesh) til et 2D-fotavtrykk (polygon) i verdenskoordinater.
 */
export function computeFootprintPolygon(params: ComputeFootprintPolygonParams): IPolygon {
  const { vertexData, indexData, transform, expressId, globalId } = params;

  // Extract positions and normals
  const positions: number[][] = [];
  const normals: number[][] = [];
  for (let i = 0; i < vertexData.length; i += 6) {
    positions.push([vertexData[i], vertexData[i + 1], vertexData[i + 2]]);
    normals.push([vertexData[i + 3], vertexData[i + 4], vertexData[i + 5]]);
  }

  // Apply transform to position (w=1)
  const applyTransform = (point: number[], matrix: readonly number[]): number[] => {
    const x = point[0],
      y = point[1],
      z = point[2];
    let xNew = matrix[0] * x + matrix[4] * y + matrix[8] * z + matrix[12];
    let yNew = matrix[1] * x + matrix[5] * y + matrix[9] * z + matrix[13];
    let zNew = matrix[2] * x + matrix[6] * y + matrix[10] * z + matrix[14];
    const w = matrix[3] * x + matrix[7] * y + matrix[11] * z + matrix[15];
    if (w !== 0 && w !== 1) {
      xNew /= w;
      yNew /= w;
      zNew /= w;
    }
    return [xNew, yNew, zNew];
  };

  const transformedPositions = positions.map((p) => applyTransform(p, transform));

  // Apply to normal (3x3, w=0, normalize)
  const applyNormal = (normal: number[], matrix: readonly number[]): number[] => {
    const x = normal[0],
      y = normal[1],
      z = normal[2];
    const xNew = matrix[0] * x + matrix[4] * y + matrix[8] * z;
    const yNew = matrix[1] * x + matrix[5] * y + matrix[9] * z;
    const zNew = matrix[2] * x + matrix[6] * y + matrix[10] * z;
    const length = Math.sqrt(xNew ** 2 + yNew ** 2 + zNew ** 2);
    if (length > 0) {
      return [xNew / length, yNew / length, zNew / length];
    }
    return [0, 0, 0];
  };

  const transformedNormals = normals.map((n) => applyNormal(n, transform));

  // Find bottom triangles (avg normal y < -0.9)
  const bottomTriangles: number[][] = [];
  for (let i = 0; i < indexData.length; i += 3) {
    const idx0 = indexData[i],
      idx1 = indexData[i + 1],
      idx2 = indexData[i + 2];
    const n0 = transformedNormals[idx0];
    const n1 = transformedNormals[idx1];
    const n2 = transformedNormals[idx2];
    const avgN = [
      (n0[0] + n1[0] + n2[0]) / 3,
      (n0[1] + n1[1] + n2[1]) / 3,
      (n0[2] + n1[2] + n2[2]) / 3,
    ];
    const length = Math.sqrt(avgN[0] ** 2 + avgN[1] ** 2 + avgN[2] ** 2);
    const normAvg = length > 0 ? avgN.map((n) => n / length) : [0, 0, 0];
    if (normAvg[1] < -0.9) {
      // pointing down in Y
      bottomTriangles.push([idx0, idx1, idx2]);
    }
  }

  if (bottomTriangles.length === 0) {
    // Fallback: use convex hull of all projected points if no bottom found
    console.warn(`No bottom faces found for ${globalId}, falling back to full projection hull`);
    const projectedPoints = transformedPositions.map((p) => [p[0], p[2]] as IPolygonPoint);
    return computeConvexHull(projectedPoints);
  }

  // Projected points X Z (Y up)
  const projectedPoints = transformedPositions.map((p) => [p[0], p[2]] as IPolygonPoint);

  // Collect boundary edges
  const edgeCount = new Map<string, number>();
  for (const tri of bottomTriangles) {
    const edges: [number, number][] = [
      [Math.min(tri[0], tri[1]), Math.max(tri[0], tri[1])],
      [Math.min(tri[1], tri[2]), Math.max(tri[1], tri[2])],
      [Math.min(tri[2], tri[0]), Math.max(tri[2], tri[0])],
    ];
    for (const edge of edges) {
      const key = edge.join(",");
      edgeCount.set(key, (edgeCount.get(key) || 0) + 1);
    }
  }

  const boundaryEdges: [number, number][] = [];
  edgeCount.forEach((count, key) => {
    if (count === 1) {
      const [a, b] = key.split(",").map(Number);
      boundaryEdges.push([a, b]);
    }
  });

  if (boundaryEdges.length === 0) {
    return [];
  }

  // Build graph
  const graph = new Map<number, number[]>();
  for (const [a, b] of boundaryEdges) {
    if (!graph.has(a)) graph.set(a, []);
    if (!graph.has(b)) graph.set(b, []);
    graph.get(a)!.push(b);
    graph.get(b)!.push(a);
  }

  // Sort neighbors by angle for consistent ordering (CCW)
  const getAngle = (p0: IPolygonPoint, p1: IPolygonPoint) =>
    Math.atan2(p1[1] - p0[1], p1[0] - p0[0]);
  graph.forEach((neighbors, key) => {
    const p0 = projectedPoints[key];
    neighbors.sort((a, b) => getAngle(p0, projectedPoints[a]) - getAngle(p0, projectedPoints[b]));
  });

  // Walk to build polygon (assume single loop, start from first edge)
  const start = boundaryEdges[0][0];
  const polygonIndices: number[] = [start];
  let current = graph.get(start)![0];
  let prev = start;
  while (current !== start && current !== undefined) {
    polygonIndices.push(current);
    const nextCandidates = graph.get(current)!.filter((n) => n !== prev);
    if (nextCandidates.length === 0) break;
    prev = current;
    current = nextCandidates[0]; // Take first (sorted)
  }

  // If not closed, warn
  if (current !== start) {
    console.warn(`Polygon not closed for ${globalId}, using convex hull fallback`);
    const points = polygonIndices.map((idx) => projectedPoints[idx]);
    return computeConvexHull(points);
  }

  const polygon: IPolygon = polygonIndices.map((idx) => projectedPoints[idx]);
  polygon.push(polygon[0]); // close

  return polygon;
}

// Helper for convex hull fallback (Graham scan CCW)
function computeConvexHull(points: IPolygonPoint[]): IPolygon {
  if (points.length < 3) return points;

  // Find lowest y, lowest x
  let minIdx = 0;
  for (let i = 1; i < points.length; i++) {
    if (
      points[i][1] < points[minIdx][1] ||
      (points[i][1] === points[minIdx][1] && points[i][0] < points[minIdx][0])
    ) {
      minIdx = i;
    }
  }
  [points[0], points[minIdx]] = [points[minIdx], points[0]];

  const p0 = points[0];

  // Sort by polar angle, distance
  points.slice(1).sort((a, b) => {
    const cross = (a[0] - p0[0]) * (b[1] - p0[1]) - (b[0] - p0[0]) * (a[1] - p0[1]);
    if (cross === 0) {
      const da = (a[0] - p0[0]) ** 2 + (a[1] - p0[1]) ** 2;
      const db = (b[0] - p0[0]) ** 2 + (b[1] - p0[1]) ** 2;
      return da - db;
    }
    return -cross;
  });

  // Build hull
  const hull: IPolygonPoint[] = [points[0], points[1]];
  for (let i = 2; i < points.length; i++) {
    while (hull.length >= 2) {
      const o = hull[hull.length - 2];
      const a = hull[hull.length - 1];
      const b = points[i];
      const cross = (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
      if (cross <= 0) {
        hull.pop();
      } else {
        break;
      }
    }
    hull.push(points[i]);
  }

  hull.push(hull[0]); // close
  return hull;
}
